import React, { useEffect, useState, useRef } from 'react';
import {
    Paper,
    Grid,
    Typography,
    Checkbox,
    ListItem,
    ListItemAvatar,
    Avatar,
    Chip,
    CircularProgress,
    InputBase,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/WarningRounded';
import DoneIcon from '@mui/icons-material/Done';
import CancelIcon from '@mui/icons-material/Cancel';
import { EmployeeFilterMenu } from '../core';
import { connect } from 'react-redux';
import { FixedSizeList } from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer";
import { Button } from 'rsuite';
import { useTranslation } from 'react-i18next';
import './SearchMultiSelect.scss';

function addAvatarLetters(listArray) {
    return listArray.map(listItem => ({
        ...listItem,
        firstNameChar: listItem.firstName ? listItem.firstName.charAt(0).toUpperCase() : 'U',
        lastNameChar: listItem.lastName ? listItem.lastName.charAt(0).toUpperCase() : '',
        label: `${listItem.firstName} ${listItem.lastName}`,
        value: listItem.employeeId,
    }));
}

function SearchMultiSelect(props) {
    const { employeesToOfferList, handleSelection, isPrivateEmployeeNamesLoading, isShowWarnings, selection = [], handleFilterChange = () => {}, isShowPreview = false, onClick = () => { }, height = 420 } = props;
    const { t } = useTranslation('employmentBillboard');
    const [filteredListArray, setFilteredListArray] = useState([]);
    const [checked, setChecked] = useState(selection.map(item => item.value));
    const [searchString, setSearchString] = useState("");
    const listRef = useRef(null);

    const handleSearchInputChange = async (value) => {
        setSearchString(value);
    };

    const onClickClear = () => {
        setSearchString("");
        handleFilterChange({ searchString: "" })
    }

    const onClickSearch = async (searchString) => {
        handleFilterChange({ searchString })
    }

    const handleToggle = item => {
        const currentIndex = checked.indexOf(item.value);
        const newChecked = [...checked];
        let selectionIndex = selection.findIndex(ele => ele.value === item.value)
        let newSelection = [...selection]
        if (currentIndex === -1 && selectionIndex === -1) {
            newChecked.push(item.value);
            newSelection.push(item);
        } else {
            newChecked.splice(currentIndex, 1);
            newSelection.splice(selectionIndex, 1);
        }

        setChecked(newChecked);

        // TODO: Refactor this to use array of int instead of array of object with value of ints
        handleSelection(newSelection);
    };

    const scrollToEmployee = (id) => {
        const index = filteredListArray.findIndex(emp => emp.employeeId === id);
        if (listRef.current && index >= 0) {
            listRef.current.scrollToItem(index, 'start');
        }
    };
    const renderListItem = ({ index, style }) => {
        const item = filteredListArray[index];
        return (
            <React.Fragment key={item.employeeId}>
                <ListItem style={{...style, justifyContent: 'space-between'}} disabled={!item.isValid} onClick={() => onClick(item)}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            onClick={() => handleToggle(item)}
                            checked={checked.includes(item.value)}
                            color='primary'
                            disabled={!item.isValid || !item.employeeId} />
                        <ListItemAvatar>
                            <Avatar alt={item.label} sizes='small'>
                                {`${item.firstNameChar.toUpperCase()}${item.lastNameChar.toUpperCase()}`}
                            </Avatar>
                        </ListItemAvatar>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center'}}>
                            <Typography>{item.label}</Typography>
                            {!item.isValidated ? <CircularProgress size={14} /> : null}
                        </div>
                    </div>
                    {!item.isValid ? (
                        <WarningIcon
                            fontSize='larger'
                            style={{ color: '#ffcc00' }} />)
                        : null}
                </ListItem>
            </React.Fragment>
    )};

    const searchBar = (
        <Paper style={{ width: '100%', display: 'flex', height: '48px', justifyContent: 'space-between' }} elevation={1}>
            <InputBase
                onChange={(event) => handleSearchInputChange(event.target.value)}
                style={{ width: '89%', paddingLeft: '20px' }}
                placeholder={'Search'}
                value={searchString}
                onKeyDown={(event) => {
                    if (event.key === 'Enter')
                        onClickSearch(searchString);
                 }}
            />
            <Button className='sms-clear-btn' hidden={!searchString} onClick={onClickClear}>{t('employeeSidePanel.privateEmployeeFilters.clearBtnLabel')}</Button>
            <Button className='sms-search-btn' onClick={() => onClickSearch(searchString)} disabled={!searchString}><SearchIcon /></Button>
        </Paper>
    );
    
    const filterButton = (
        <EmployeeFilterMenu
            onFilterChange={(filters) => handleFilterChange(filters)}
            t={t}
        />
    )

    const list = () => {
        return (
            <AutoSizer>
                {({ height, width }) => {
                    
                    return (
                        <FixedSizeList
                            className="List"
                            height={height}
                            width={width}
                            itemSize={55}
                            itemCount={filteredListArray.length}
                        >
                            {renderListItem}
                        </FixedSizeList>
                    )
                }}
            </AutoSizer>
    )};

    useEffect(() => {
        let employees = employeesToOfferList.employees;
        if (!isShowWarnings) {
            employees = employees.filter(x => x.isValid);
        }
        setFilteredListArray(addAvatarLetters(employees));
    }, [employeesToOfferList.employees, isShowWarnings]);
    return (
        <Grid container>
            <Grid item xs={12} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <div style={{flexGrow: 50}}>
                    {searchBar}
                </div>
                <div style={{flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    {filterButton}
                </div>
            </Grid>
            <Grid item xs={12} hidden={!isShowPreview}>
                <div className="preview-root">
                    {
                        selection.map((x, i) => {
                            const employee = employeesToOfferList.employees.find(ee => ee.employeeId == x.employeeId);
                            return (
                            <Chip
                                icon={ employee?.isValidated ? (employee.isValid ? <DoneIcon className='sms-green-icon'/> : <CancelIcon className='sms-red-icon'/>) : <CircularProgress size={14} /> }
                                label={x.label}
                                onClick={() => scrollToEmployee(x.employeeId)}
                                onDelete={() => {
                                    var updatedList = selection.filter(ee => ee != x);
                                    handleSelection(updatedList);
                                    setChecked(updatedList.map(ee => ee.value))
                                }}
                                variant="outlined"
                                className={employee?.isValidated ? (`sms-employee-summary-chip-color-${employee.isValid ? 'success' : 'fail'}`) : null}
                            />
                        )})
                    }
                </div>
            </Grid>
            <Grid item xs={12} className="sms-list">
                {
                    isPrivateEmployeeNamesLoading
                        ? <div className='eps-list-loading'><CircularProgress /></div>
                        : filteredListArray.length > 0 ?
                            <div style={{ height: `${height}px` }}>
                                {list()}
                            </div>
                            : <div style={{ color: '#999', paddingTop: 20, textAlign: 'center', fontStyle: 'italic' }}>{t('employeeSidePanel.privateEmployeeSection.noEmployeesLabel')}</div>
                }
            </Grid>
            <Grid item xs={12}>
                <div style={{ display: 'flex', justifyContent:'space-between' }}>
                    <span style={{ display: 'flex', gap: '5px' }}>
                        {t('employeeSidePanel.privateEmployeeSection.validatedEmployeesLabel')}{ employeesToOfferList.employees.filter(x => x.isValidated).length }
                        {employeesToOfferList.employees.filter(x => x.isValidated).length < employeesToOfferList.employees.length ? <CircularProgress size={14} /> : null}
                    </span>
                    <span>
                        {t('employeeSidePanel.privateEmployeeSection.totalEmployeesLabel')}{ employeesToOfferList.employees.length }
                    </span>
                </div>
            </Grid>
        </Grid>
    );
}

const mapStateToProps = state => {
    return {
        employeesToOfferList: state.employeesToOfferList,
        isPrivateEmployeeNamesLoading: state.privateEmployeeNamesLoadingState,

    }
}
export default connect(mapStateToProps)(SearchMultiSelect);
